/**
 * Investors & Capital Raising — 13 documents.
 *
 * None of these entries carry a template, and none ever will. Selling equity or a convertible
 * instrument is a regulated activity: how the instrument may be offered, to whom, and what must be
 * documented are legal questions that vary by where you and your investors are. This category is
 * guidance only — it exists so a founder can walk into the conversation understanding what they are
 * being asked to sign and what it costs them, and then take the drafting to counsel.
 */
export const INVESTORS_DOCS = [
  {
    id: "safe",
    name: "SAFE (Simple Agreement for Future Equity)",
    category: "investors",
    subcategory: "Convertible instruments",
    aliases: ["Simple Agreement for Future Equity", "Safe Note", "Pre-Money SAFE", "Post-Money SAFE"],
    stages: ["pre-launch", "launch", "fundraising"],
    importance: "common",
    entityTypes: ["corporation"],
    parties: "Company + Investor",
    whenYouNeedIt:
      "When you are taking money before anyone has agreed what the company is worth — usually the first outside cheques, from angels, industry operators or customers who love the product.",
    whatItDoes:
      "The investor pays now and receives nothing yet. The document fixes the terms on which that money later turns into shares, normally when you close a priced round; until then the investor holds a contract, not stock.",
    whyItMatters:
      "This is the cheapest paper you will ever sign and the most expensive to misread. Three lines — the cap, the discount, and whether the instrument is pre-money or post-money — decide how much of your company the investor ends up owning, and founders routinely sign several at different numbers without ever modelling the combined effect. It is also a security, whatever it is called and however short it is, so how you may offer it and who you may offer it to are legal questions before they are commercial ones. Simple to sign is not the same as simple in consequence.",
    majorTerms: [
      "Valuation cap",
      "Discount rate",
      "Pre-money or post-money structure",
      "What counts as a triggering equity financing",
      "Treatment on a sale of the company before conversion",
      "Most-favoured-nation provision",
      "Pro rata rights in the next round",
      "Information rights, if any",
      "Amendment by a majority of holders",
      "Transferability of the instrument",
    ],
    clausesToWatch: [
      {
        clause: "Valuation cap",
        why: "It sets the highest price at which the money converts, so a low cap agreed casually on a small early cheque can hand over far more of the company than the amount raised suggests.",
      },
      {
        clause: "Post-money versus pre-money definition",
        why: "It decides whether later investors dilute the holder or only you — on a post-money instrument, every additional dollar raised on the same terms comes out of the founders.",
      },
      {
        clause: "Discount and how it interacts with the cap",
        why: "Whichever is better for the investor normally applies, so founders model one outcome and get the other.",
      },
      {
        clause: "Liquidity event before conversion",
        why: "This says what the investor receives if you sell the brand before ever raising a priced round, and a multiple payable on exit changes the economics of a small early cheque entirely.",
      },
      {
        clause: "Pro rata rights",
        why: "An early, small investor entitled to keep their percentage can crowd out the lead investor you need in the next round.",
      },
      {
        clause: "Most-favoured-nation provision",
        why: "A later, better instrument silently upgrades the earlier ones, so terms you thought were settled move without a conversation.",
      },
      {
        clause: "Amendment by holder majority",
        why: "It determines whether one large holder can change the terms for everyone, including you.",
      },
    ],
    cpgIssues: [
      "CPG money often comes from inside the trade: a distributor, a broker, a co-packer's owner, a retail buyer's friend. Any commercial expectation attached to the cheque — exclusivity, a production commitment, a listing — belongs in a separate commercial agreement with its own term and termination, not implied by an investment.",
      "Brands raise from customers who found them on shelf, and a round assembled from many small consumer cheques creates a long holder list that every later round and every buyer has to work through.",
      "Inventory-heavy businesses burn this money on working capital — cans, ingredients, a co-packer deposit — which is the most expensive possible way to fund a purchase order that inventory or purchase-order financing might have funded without giving up any ownership.",
      "The cap you agree at kitchen-table stage follows you for years: a founder three summers into real trade spend still converts that first cheque at a number set before the first production run.",
      "Seasonality means you usually raise at the wrong point in your own cycle. A cap agreed just after a strong summer sell-through looks very different two quarters later, and neither number is wrong — they are just different months.",
    ],
    founderMistakes: [
      "Signing six instruments at four different caps over eighteen months, then discovering at the priced round that the combined conversion took far more of the company than the sum of the cheques implied.",
      "Treating a SAFE as 'not real equity yet' and leaving it off the cap table, so the first serious investor was working from a different model than the founder.",
      "Accepting a distributor's cheque alongside a verbal promise of regional exclusivity, then having no way to unwind either one.",
      "Using a version downloaded from a blog and never checking whether the pre- or post-money mechanics matched what was said in the room.",
      "Raising equity money to buy inventory against a confirmed retailer purchase order, when the purchase order itself could have supported cheaper, non-dilutive funding.",
    ],
    negotiationPoints: [
      "Ask to model every outstanding instrument together, at the cap and at the discount, before signing another one.",
      "Ask for one consistent cap across a single round rather than agreeing a new number with each investor.",
      "Ask that any commercial expectation — exclusivity, supply, listing, production priority — is written into a separate commercial agreement.",
      "Ask what happens on a sale before conversion, and make sure you can live with that outcome at a modest exit price.",
      "Ask whether pro rata rights are warranted at this cheque size; they are easy to give and very hard to take back.",
    ],
    redFlags: [
      "A cap agreed verbally and papered later, or different numbers in the email and in the document.",
      "Any commercial commitment — exclusivity, supply, distribution — living inside the investment paper.",
      "An instrument that pays a multiple of the investment on a sale of the company.",
      "Pressure to close before counsel has read it because it is 'the standard form'.",
      "Side letters to individual investors that you have not read alongside the main document.",
      "A promise of a board seat, a veto or an approval right attached to a small early cheque.",
    ],
    beforeYouSign: [
      "A current, accurate cap table that includes every outstanding instrument.",
      "A model showing what a priced round at a realistic valuation does to your ownership once everything converts at once.",
      "Counsel's confirmation of how this instrument may be offered and to whom.",
      "A written record of what each investor has been told about the business.",
    ],
    related: [
      "convertible-note",
      "preferred-stock-purchase-agreement",
      "term-sheet",
      "cap-table-documentation",
      "investor-nda",
      "founder-stock-documents",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "A SAFE is a security, so how it may be offered and to whom are legal questions, and its three conversion terms decide how much of your company you have already sold.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "convertible-note",
    name: "Convertible Note",
    category: "investors",
    subcategory: "Convertible instruments",
    aliases: ["Convertible Promissory Note", "Bridge Note", "Convertible Debt", "Seed Note"],
    stages: ["pre-launch", "launch", "growth", "fundraising"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Company (borrower) + Investor (lender)",
    whenYouNeedIt:
      "When you want early money without setting a price, but the investor wants to be a lender until conversion — often a bridge between rounds, or a raise where the investor wants interest and a date.",
    whatItDoes:
      "It is a loan that everyone expects to become equity. The company owes the money back, with interest, on a stated maturity date, and the note sets the terms on which the balance converts into ownership instead — usually when a priced round closes.",
    whyItMatters:
      "The difference between a note and a SAFE is the maturity date: a note is money you owe on a specific day. If that day arrives before a priced round has closed, you are negotiating with a creditor about the future of your own company at the worst possible moment. In CPG a raise slips for reasons that have nothing to do with the raise — a delayed co-packer run, a failed stability result, a retailer reset you missed by two weeks — and the maturity date does not move with your production calendar. Accrued interest converts too, so the investor ends up with more than the cheque implied.",
    majorTerms: [
      "Principal amount",
      "Interest rate and whether interest converts",
      "Maturity date",
      "Valuation cap",
      "Discount rate",
      "Automatic versus optional conversion triggers",
      "What happens at maturity if no round has closed",
      "Treatment on a sale of the company",
      "Security over company assets, if any",
      "Subordination to other lenders",
      "Amendment by a majority of noteholders",
      "Prepayment rights",
    ],
    clausesToWatch: [
      {
        clause: "Maturity date",
        why: "It is the day the note becomes a debt you have to deal with, and the holder's leverage over the company is at its highest exactly when your raise is late.",
      },
      {
        clause: "What happens at maturity with no financing",
        why: "If the note is silent, you are renegotiating from the weakest position you will ever be in, with no agreed fallback.",
      },
      {
        clause: "Security over company assets",
        why: "A secured note is a completely different instrument from an unsecured one, and an inventory-heavy brand has assets a lender can reach — what that means in practice is a question for counsel before signing.",
      },
      {
        clause: "Automatic versus optional conversion",
        why: "A holder who can choose whether to convert may prefer to stay a creditor in precisely the scenario where you need them to become a shareholder.",
      },
      {
        clause: "Accrued interest on conversion",
        why: "Interest usually converts alongside principal, so the investor's final share count is larger than the founder's model based on the cheque.",
      },
      {
        clause: "Amendment threshold across noteholders",
        why: "It decides whether a defined majority can extend maturity for everyone or whether a single holdout can force the issue.",
      },
      {
        clause: "Subordination",
        why: "A future lender will normally want this note behind it, and a holder who refuses at that moment can block the working capital you need.",
      },
    ],
    cpgIssues: [
      "CPG raises slip with the production calendar. A delayed run, a failed shelf-life result or a missed authorisation pushes a round a full season, and the maturity date is indifferent to all of it.",
      "A note secured against company assets reaches the inventory, which for a brand is most of the balance sheet and is usually sitting in a third party's warehouse under arrangements that were written without this note in mind.",
      "Industry investors — a distributor's principal, a co-packer's owner — sometimes prefer a note precisely because it makes them a creditor of a company they also supply, which is an uncomfortable position for both sides during a quality dispute.",
      "Friends-and-family notes written on one page, with no maturity plan and no interest tracking, surface years later in diligence with a balance nobody can reconcile.",
      "Note money buys cans and ingredients that are consumed within a season; the dilution on conversion is permanent and the working capital is not.",
    ],
    founderMistakes: [
      "Letting notes mature during the slow season and accepting an extension entirely on the holder's terms.",
      "Stacking notes and SAFEs together, then finding that accrued interest and three different caps combined to take far more of the company than modelled.",
      "Signing a note that was quietly secured against company assets, which then had to be cleared before any lender or buyer would move.",
      "Never tracking accrued interest, so the conversion figure in diligence did not match the founder's spreadsheet.",
    ],
    negotiationPoints: [
      "Ask for a maturity date that sits a full production and selling season beyond your realistic raise date.",
      "Ask for automatic conversion on a qualified financing rather than leaving the choice with the holder.",
      "Ask that the note is unsecured, and if it cannot be, have counsel explain exactly what it reaches before you sign.",
      "Ask for an amendment provision letting a defined majority extend maturity, so one holder cannot hold the company up.",
      "Ask to see a single combined model of every note and SAFE converting at the same time.",
    ],
    redFlags: [
      "Security over company assets on an early-stage note.",
      "No stated outcome at maturity.",
      "A maturity date inside twelve months when your next raise depends on a season that has not happened yet.",
      "Interest that converts at the cap without being shown in the summary you were given.",
      "A holder demanding board seats or approval rights that belong to a priced round.",
    ],
    beforeYouSign: [
      "A combined conversion model covering every outstanding instrument.",
      "A fundraising calendar built around your production and selling season, not around a spreadsheet.",
      "Counsel's confirmation of how this instrument may be offered and to whom.",
      "A list of every other claim on company assets, including lenders, equipment lessors and warehouse arrangements.",
    ],
    related: [
      "safe",
      "promissory-note-investor",
      "preferred-stock-purchase-agreement",
      "term-sheet",
      "cap-table-documentation",
      "business-loan-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "This is both a security and a debt of the company, and the maturity, security and subordination terms decide what happens to you if the next raise is late.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "promissory-note-investor",
    name: "Promissory Note (Investor)",
    category: "investors",
    subcategory: "Convertible instruments",
    aliases: ["Investor Loan Note", "Non-Convertible Investor Note", "Bridge Loan from Investor"],
    stages: ["launch", "growth", "fundraising"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Company + Investor (as lender)",
    whenYouNeedIt:
      "When an investor, board member or family member is lending the company money rather than buying into it — a bridge to a purchase order, a season's inventory, or a gap before a round closes.",
    whatItDoes:
      "Records a straight loan: how much, at what interest, repayable when, and what happens if it is not repaid. Nothing converts and no ownership changes hands.",
    whyItMatters:
      "This is the document founders are most likely to skip, because the money comes from someone they trust. An undocumented loan from a parent or a board member shows up years later as an unexplained liability, and in the worst case as an argument about whether it was ever a loan at all rather than an investment. Money from someone who also holds equity, or expects to, needs its character fixed in writing at the time — reconstructing the intention afterwards, when the company is finally worth something, is where relationships end.",
    majorTerms: [
      "Principal amount and what it funds",
      "Interest rate",
      "Repayment schedule and any interest-only period",
      "Maturity date",
      "Prepayment rights",
      "Security over company assets, if any",
      "Personal guarantee, if any",
      "Subordination to other lenders",
      "Events of default and acceleration",
      "Restrictions on assignment",
    ],
    clausesToWatch: [
      {
        clause: "Security over company assets",
        why: "An investor loan secured against inventory or receivables sits ahead of every shareholder, and its practical effect on your ability to operate is a question for counsel before signing.",
      },
      {
        clause: "Subordination",
        why: "A future working capital lender will normally require this note to sit behind it, and a holder who will not agree can block the financing you need.",
      },
      {
        clause: "Acceleration on default",
        why: "Immediate acceleration on a missed payment turns a short cash gap into a demand for the whole balance during the month you had least of it.",
      },
      {
        clause: "Assignment",
        why: "A note sold on to a third party puts someone you never chose in the lender's seat, with the same rights and none of the relationship.",
      },
      {
        clause: "Conversion language slipped into a plain note",
        why: "Vague wording about what happens 'in the event of a financing' creates an expectation of equity with no agreed mechanism, which is a fight waiting for your next round.",
      },
      {
        clause: "Personal guarantee",
        why: "A guarantee on money lent by a friend converts a company debt into your own exposure and outlives the goodwill that created it.",
      },
    ],
    cpgIssues: [
      "Repayment schedules rarely match the cash conversion cycle: you pay the co-packer before production and the retailer pays you long after delivery, so monthly repayment starting at production is repayment out of nothing.",
      "A note funding one specific production run should be sized and timed to that run's sell-through, not to a calendar month that ignores your season.",
      "A lender who is also your ingredient supplier, freight broker or distributor now has two relationships with you, and they pull against each other the first time a shipment is late.",
      "Several small notes taken across a single season quietly become the company's largest liability, and no one document shows the total.",
      "An investor note that is assigned means the person you shook hands with at a trade show is no longer the person you owe.",
    ],
    founderMistakes: [
      "Taking money from a relative with nothing in writing and later arguing about whether it was a loan or an investment.",
      "Agreeing repayment to start thirty days after production when the retailer paid in ninety.",
      "Missing one payment on a note with immediate acceleration and no cure period.",
      "Letting several small notes accumulate with no central record of balances or maturity dates.",
    ],
    negotiationPoints: [
      "Ask for a repayment schedule tied to when the funded inventory actually converts to cash.",
      "Ask for a grace period and written notice before any default.",
      "Ask to prepay in full without penalty when a season goes well.",
      "Ask that the note is unsecured and carries no personal guarantee.",
      "Ask that it cannot be assigned to a third party without your consent.",
    ],
    redFlags: [
      "No written document at all.",
      "Acceleration with no notice and no cure period.",
      "A personal guarantee attached to a modest loan from someone you know.",
      "Free assignment to anyone the lender chooses.",
      "Language hinting at conversion to equity with no defined price or mechanism.",
    ],
    beforeYouSign: [
      "A thirteen-week cash view showing when repayment is genuinely possible.",
      "A complete list of the company's existing debts and who holds them.",
      "Counsel's read if any security, guarantee or conversion language is involved.",
    ],
    related: [
      "convertible-note",
      "promissory-note",
      "founder-loan-agreement",
      "shareholder-loan-agreement",
      "cap-table-documentation",
      "security-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Money taken from an investor on loose paper becomes a dispute about what it was, and the security, guarantee and subordination terms decide both the company's and your own exposure.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "preferred-stock-purchase-agreement",
    name: "Preferred Stock Purchase Agreement",
    category: "investors",
    subcategory: "Priced rounds",
    aliases: ["Series Seed Documents", "Series A Purchase Agreement", "Equity Financing Agreement", "Priced Round"],
    stages: ["growth", "fundraising"],
    importance: "advanced",
    entityTypes: ["corporation"],
    parties: "Company + Investors (with founders signing certain provisions personally)",
    whenYouNeedIt:
      "When you agree a price for the company and sell shares at it — the first institutional round, or the round where every SAFE and note you have signed finally converts.",
    whatItDoes:
      "Sells a newly created class of shares at an agreed price. Unlike a SAFE or a note, nothing is deferred: valuation, ownership, board composition and investor rights are all fixed at signing, inside a package of documents that this agreement sits at the centre of.",
    whyItMatters:
      "A priced round is where every deferred decision arrives at once. All outstanding SAFEs and notes convert here, often at several different caps and with accrued interest, and the resulting dilution is routinely far larger than a founder modelled from the cheque sizes alone. The shares being sold are not the same as yours: preferred shares carry rights on a sale, on new issuances and on decisions the board takes, and that is what a founder is actually giving up in exchange for the money. Terms vary widely by round size, category and investor type, and this is a counsel document from the first draft to the last signature.",
    majorTerms: [
      "Price per share and pre-money valuation",
      "Amount raised and any tranches",
      "Option pool size and whether it is created before or after the price",
      "Liquidation preference and whether it participates",
      "Anti-dilution protection",
      "Board composition and who appoints whom",
      "Protective provisions requiring investor consent",
      "Dividend provisions",
      "Conversion of outstanding SAFEs and notes",
      "Representations and warranties from the company and the founders",
      "Closing conditions and disclosure schedule",
      "Use of proceeds",
    ],
    clausesToWatch: [
      {
        clause: "Liquidation preference",
        why: "It decides who gets paid first on a sale and how much, and at a modest exit price a stacked or participating preference can absorb most of the proceeds before common shareholders see anything.",
      },
      {
        clause: "Option pool sizing and where it sits",
        why: "A pool created before the price is set comes out of the founders alone, which can move real ownership more than the headline valuation does.",
      },
      {
        clause: "Protective provisions",
        why: "This is the list of things the company cannot do without investor consent, and written broadly it reaches ordinary operating decisions like taking inventory financing or signing a distributor.",
      },
      {
        clause: "Anti-dilution protection",
        why: "It determines what happens to the investor's position if you later raise at a lower price, which in a seasonal business is a live possibility rather than a theoretical one.",
      },
      {
        clause: "Board composition",
        why: "This is the moment control starts to move, and the seat count is far harder to renegotiate later than the valuation.",
      },
      {
        clause: "Founder representations and warranties",
        why: "You are personally standing behind statements about IP ownership, contracts and compliance, so every unassigned logo and unsigned co-packer agreement becomes your exposure.",
      },
      {
        clause: "Conversion mechanics for existing instruments",
        why: "The interaction of caps, discounts, accrued interest and the new pool is where the real dilution is decided, and it is rarely visible in the term sheet.",
      },
    ],
    cpgIssues: [
      "The representations cover exactly the paperwork CPG founders most often lack: formula ownership, executed co-packer and distributor agreements, trademarks registered in the company's name, contractor IP assignments and quality records.",
      "Protective provisions drafted for a software company can accidentally require investor consent before you take inventory or equipment financing, which for a brand is ordinary operating finance rather than a strategic event.",
      "A strategic investor from inside the trade — a distributor, a retailer's investment arm, a co-packer — may want commercial rights alongside the shares; those belong in a separate commercial agreement with its own term and its own exit.",
      "Trade spend, deductions, spoils and free fill make revenue lumpy and sometimes restated, so a valuation and a set of warranties built on gross rather than net revenue cause trouble at the first true-up.",
      "Working-capital-heavy brands often raise equity to fund inventory that debt could have funded, and the priced round is the moment that decision becomes permanent.",
    ],
    founderMistakes: [
      "Negotiating the valuation hard and then accepting a preference and pool structure that mattered far more than the headline number.",
      "Arriving at the round with unassigned artwork and no signed co-packer agreement, and giving the warranties anyway.",
      "Never modelling the conversion of existing instruments, and learning after closing how much had actually gone.",
      "Accepting protective provisions that later required investor consent for a small equipment lease.",
      "Letting a strategic investor fold category exclusivity into the round rather than negotiating it as its own commercial deal.",
    ],
    negotiationPoints: [
      "Ask for a fully-diluted model, before and after, that includes every SAFE, note, option and the new pool.",
      "Ask for the option pool to be sized against a real hiring plan, and discuss where it sits relative to the price.",
      "Ask for protective provisions limited to genuinely major decisions, with ordinary trade and inventory financing carved out.",
      "Ask for a simple, non-participating preference and have counsel walk you through what it does at a modest exit price.",
      "Ask that any commercial arrangement with an investor is separately documented and separately terminable.",
      "Ask for a proper disclosure schedule process so warranties are given against what is actually true.",
    ],
    redFlags: [
      "Preference terms stacked beyond a simple return with no explanation of what they do at a low exit price.",
      "An option pool created entirely pre-money with no discussion.",
      "Protective provisions reaching day-to-day operating decisions.",
      "Personal warranties from founders with no cap and no survival limit.",
      "A strategic investor conditioning the money on commercial terms.",
      "Pressure to sign before the disclosure schedule is complete.",
    ],
    beforeYouSign: [
      "A complete cap table including every outstanding instrument and its conversion terms.",
      "Signed IP assignments from every founder, employee and contractor.",
      "Executed co-packer, distributor and retailer vendor agreements.",
      "Trademark registrations confirmed in the company's exact legal name.",
      "A disclosure schedule you have read line by line.",
      "A model of the exit outcome at several different prices, not just the good one.",
    ],
    related: [
      "safe",
      "convertible-note",
      "investor-rights-agreement",
      "rofr-co-sale-agreement",
      "term-sheet",
      "cap-table-documentation",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "This is a securities transaction with personal warranties from the founders and permanent changes to control, and no part of it should be papered without counsel.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "subscription-agreement",
    name: "Subscription Agreement",
    category: "investors",
    subcategory: "Priced rounds",
    aliases: ["Investment Subscription Agreement", "Securities Subscription Agreement", "Share Subscription"],
    stages: ["launch", "growth", "fundraising"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Company + Subscribing Investor",
    whenYouNeedIt:
      "When an investor is committing to buy into the company and you need their signed commitment, their own information and their acknowledgements in one place — common in founder-led raises and alongside many SAFE and note rounds.",
    whatItDoes:
      "Records the investor's offer to buy, the company's acceptance, what is being bought and for how much, and the statements the investor makes about themselves and about the information they were given.",
    whyItMatters:
      "This is where the record of who you sold to actually lives. Who is permitted to invest, on what basis, and what you must tell them are regulated questions with different answers in different places, and this document is the permanent evidence of how you answered them. Founders who take wires without this record have no file to hand a later investor or a buyer, and reconstructing it from memory and email years afterwards is both unpleasant and expensive. Do not answer the question of who may invest from a blog post — put it to counsel before you speak to anyone.",
    majorTerms: [
      "Amount subscribed and exactly what is being bought",
      "Closing mechanics and where funds are sent",
      "The investor's statements about themselves",
      "Acknowledgement of the risks disclosed",
      "Transfer restrictions on what is bought",
      "Investor contact details and notices",
      "The company's right to reject a subscription",
      "Conditions to closing and counterpart signature",
    ],
    clausesToWatch: [
      {
        clause: "Investor qualification statements",
        why: "This is the file that evidences who you sold to, and getting it wrong is a legal problem rather than a paperwork problem.",
      },
      {
        clause: "Risk acknowledgements",
        why: "They must match what you actually told the investor, because a mismatch between the deck and the acknowledgement is the first thing anyone reviewing the raise will find.",
      },
      {
        clause: "The company's right to reject",
        why: "Without it you cannot decline money from an investor you have decided you do not want on the register.",
      },
      {
        clause: "Transfer restrictions",
        why: "They control whether your investor can sell their position to a competitor, a distributor or a stranger, and silence here creates a shareholder you never chose.",
      },
      {
        clause: "Side letters",
        why: "A separate promise to one investor that contradicts the main terms will surface in the next round, usually at the worst moment and usually in writing.",
      },
    ],
    cpgIssues: [
      "Brands with a loyal customer base are tempted to raise from that base, which multiplies both the number of investors and the record-keeping, and every single cheque is a regulated transaction.",
      "A distributor or retailer subscribing for shares usually expects something commercial in return, and that expectation cannot live in the subscription paperwork — it needs its own agreement.",
      "Investors met at trade shows are frequently taken on trust, with no written record of what they were told about velocity, authorisations or margin.",
      "Projections shared informally at a pitch — a chain authorisation you expect but do not have, a distributor conversation that has not closed — become part of what the investor relied on when they wired.",
      "A crowd of small subscribers created in one enthusiastic season is a cap-table problem in every subsequent round and again at a sale.",
    ],
    founderMistakes: [
      "Taking wires before any subscription paperwork existed, then chasing signatures for months.",
      "Reusing another company's subscription document without counsel and collecting the wrong statements.",
      "Making forward-looking claims about upcoming authorisations in a deck that then formed part of the raise record.",
      "Giving one investor a side letter and forgetting it existed until the next round's diligence found it.",
    ],
    negotiationPoints: [
      "Ask counsel to design the subscription process before you speak to a single investor.",
      "Ask for one consistent package across the round rather than per-investor variations.",
      "Ask that every investor signs the same risk acknowledgements against the same materials.",
      "Ask that any side letter is disclosed to counsel and kept with the raise file.",
    ],
    redFlags: [
      "Pressure to accept money before the paperwork exists.",
      "An investor who will not complete their own statements.",
      "Different terms promised to different investors in the same round.",
      "A deck making revenue or authorisation claims you cannot evidence.",
      "Anyone offering to find investors for you in exchange for a share of what they raise, without counsel confirming that arrangement is permitted.",
    ],
    beforeYouSign: [
      "A counsel-designed subscription package for this specific raise.",
      "Every piece of written material given to investors, kept together.",
      "A complete list of who was approached, when, and what they were sent.",
    ],
    related: [
      "safe",
      "convertible-note",
      "preferred-stock-purchase-agreement",
      "investor-nda",
      "cap-table-documentation",
      "investor-rights-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Who you may sell to and what you must tell them are regulated questions, and this document is the permanent record of how you answered them.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "investor-rights-agreement",
    name: "Investor Rights Agreement",
    category: "investors",
    subcategory: "Investor rights & governance",
    aliases: ["IRA", "Information Rights Agreement", "Registration Rights Agreement"],
    stages: ["growth", "fundraising"],
    importance: "advanced",
    entityTypes: ["corporation"],
    parties: "Company + Investors + (usually) Founders",
    whenYouNeedIt:
      "At a priced round, alongside the purchase agreement — it is one of the documents in the package, not an optional extra.",
    whatItDoes:
      "Sets out what investors get beyond the shares themselves: financial reporting, inspection rights, the right to buy into future rounds, and a set of things the company promises to do and not do while they are shareholders.",
    whyItMatters:
      "The obligations in here are ongoing and operational, and they land on a company where the founder is also the finance function. Monthly reporting in a defined format is a real cost for a five-person brand whose month is distorted by trade spend and a distributor's ordering pattern. Information and inspection rights also mean your landed cost, your co-packer's identity and your retailer terms travel to people who may hold other brands in your category — food and beverage is a small industry and information moves. A covenant you cannot actually meet becomes a default, which is a different conversation entirely.",
    majorTerms: [
      "Information and reporting rights, with frequency and format",
      "Inspection rights and notice",
      "Pro rata participation rights in future rounds",
      "Registration rights",
      "Board observer rights",
      "Affirmative covenants such as insurance and IP assignments from all personnel",
      "Negative covenants",
      "Confidentiality obligations on investors",
      "Termination of rights on an exit or a qualified financing",
    ],
    clausesToWatch: [
      {
        clause: "Reporting frequency and format",
        why: "Monthly reporting in a defined format is a genuine cost for a small brand, and missing it is a breach rather than an inconvenience.",
      },
      {
        clause: "Inspection rights",
        why: "Broad inspection lets an investor ask for your co-packer contract, cost sheets and customer terms, which is a lot of visibility to hand someone who also backs other brands.",
      },
      {
        clause: "Pro rata participation rights",
        why: "Accumulated across several small investors, they can leave no room for the lead investor your next round depends on.",
      },
      {
        clause: "Affirmative covenants",
        why: "Requirements to carry stated insurance limits and to collect signed IP assignments from everyone are reasonable, but you have to actually do them or you are in breach.",
      },
      {
        clause: "Investor confidentiality",
        why: "Without an explicit obligation, nothing stops your numbers circulating inside a firm that holds a competing brand in the same set of retailers.",
      },
      {
        clause: "Termination of rights",
        why: "Rights with no end point follow the company into every future round and into a sale process, where they slow everything down.",
      },
    ],
    cpgIssues: [
      "Investors active in food and beverage frequently hold several brands, so broad information rights put your landed cost, co-packer identity and retailer terms in circulation in a very small industry.",
      "Affirmative covenants often require product liability cover at stated limits and signed IP assignments from everyone — check them against what your co-packer, your 3PL and your retailers already require so you are not carrying three different standards.",
      "Reporting formats designed for a software company do not fit a brand whose month swings on trade spend, deductions and one distributor's ordering pattern; define the metrics before you agree to report them.",
      "An observer or an investor with inspection rights hears co-packer pricing and buyer conversations, which matters if they sit on another brand's cap table.",
      "Pro rata rights held by an early trade investor can complicate a later strategic round with a competing operator who does not want them in the room.",
    ],
    founderMistakes: [
      "Agreeing monthly reporting the company never produced, creating a standing breach nobody mentioned until diligence.",
      "Giving inspection rights to an investor who also backed a direct competitor in the same accounts.",
      "Agreeing a covenant requiring signed IP assignments from all personnel and never collecting a single one.",
      "Letting pro rata rights accumulate until there was no allocation left for a lead investor.",
    ],
    negotiationPoints: [
      "Ask for reporting you can genuinely produce, in a format you define, on a frequency that matches your close process.",
      "Ask for pro rata rights limited to investors above a stated size.",
      "Ask for explicit confidentiality obligations on investors, with a carve-out where the investor holds a competing brand.",
      "Ask that rights terminate on a sale or a subsequent qualified financing.",
      "Ask for a reasonable notice period before any inspection.",
    ],
    redFlags: [
      "Unlimited inspection rights with no notice requirement.",
      "No confidentiality obligation on investors at all.",
      "Reporting obligations you already know you cannot meet.",
      "Covenants whose breach triggers rights an investor can accelerate.",
      "Observer rights granted to a firm backing a competing brand with no exclusion mechanism.",
    ],
    beforeYouSign: [
      "An honest view of what your finance function can produce every month.",
      "A list of which investors hold competing or adjacent brands.",
      "The full round package read together rather than document by document.",
    ],
    related: [
      "preferred-stock-purchase-agreement",
      "rofr-co-sale-agreement",
      "board-observer-agreement",
      "term-sheet",
      "voting-agreement",
      "shareholder-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "These are continuing obligations of the company sitting alongside a securities transaction, and a covenant you cannot meet becomes a default.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "rofr-co-sale-agreement",
    name: "Right of First Refusal and Co-Sale Agreement",
    category: "investors",
    subcategory: "Investor rights & governance",
    aliases: ["ROFR and Co-Sale", "First Refusal and Co-Sale Agreement", "Tag-Along Agreement"],
    stages: ["growth", "fundraising"],
    importance: "advanced",
    entityTypes: ["corporation"],
    parties: "Company + Investors + Founders and key holders",
    whenYouNeedIt:
      "At a priced round, as part of the package — and again any time a founder or major holder wants to sell shares before an exit.",
    whatItDoes:
      "Controls what happens when a founder or major holder wants to sell: the company and then the investors get a chance to buy the shares first, and if they decline, investors can join the sale on the same terms.",
    whyItMatters:
      "This is the document that decides whether you can ever take money off the table before a full exit. For a founder who has personally funded three years of production runs, that is not an abstract question. It also stops any holder selling a stake to an outsider — a competitor, a distributor, a co-packer — without the other shareholders having a say, which in an industry this small matters more than it sounds. The mechanics are what make it workable or not: a notice and closing process that takes months will kill any real secondary buyer before they get to the paperwork.",
    majorTerms: [
      "Which holders' shares are covered",
      "The company's right of first refusal",
      "The investors' secondary right",
      "Co-sale rights and how the pro rata allocation is calculated",
      "Permitted transfers for family and estate planning",
      "Notice periods and closing mechanics",
      "Prohibited transferees",
      "Termination on an exit or a listing",
    ],
    clausesToWatch: [
      {
        clause: "Which holders are bound",
        why: "Founders are usually bound and investors often are not, which means the restrictions run one way unless you ask.",
      },
      {
        clause: "Notice and closing timetable",
        why: "A process that runs for months will lose a genuine secondary buyer, which effectively means no founder liquidity at all.",
      },
      {
        clause: "Co-sale allocation",
        why: "Investors tagging along can shrink a founder's intended sale to a fraction of it, so the size you can actually sell is not the size you announced.",
      },
      {
        clause: "Prohibited transferees",
        why: "Without a list written in trade terms, a stake can end up with a distributor, a co-packer or a competing brand that then sees your economics from the inside.",
      },
      {
        clause: "Permitted transfers",
        why: "If estate planning, death and divorce are not addressed, a stake can move to someone the company has no relationship with and no ability to buy out.",
      },
      {
        clause: "Termination of the restrictions",
        why: "Restrictions with no end point follow you into every future transaction, including a sale process where speed is the whole point.",
      },
    ],
    cpgIssues: [
      "The prohibited-transferee list should be written in trade terms — a competing brand, a distributor, a co-packer, a retailer, an ingredient supplier — because those are the parties most likely to want a piece of a small brand.",
      "Founder secondary sales in CPG usually happen at a strategic investment rather than at a full exit, so the notice and consent process determines whether that is possible at all.",
      "A founder who has personally funded production runs may need liquidity years before an exit; if every share is locked, the only route to any cash is selling the whole company.",
      "A distributor or retailer that acquires founder shares gains visibility into your margins while sitting on the other side of your commercial negotiation.",
      "Death, divorce or a founder departure can put a stake in unexpected hands, and the permitted-transfer language is the only thing that gives the company a say.",
    ],
    founderMistakes: [
      "Agreeing a structure that made any founder liquidity impossible short of a full exit, then having no answer when a family need arose.",
      "Not restricting transfers to competitors, so a stake ended up with a company that also distributed the product.",
      "Agreeing a notice process so slow that a real secondary buyer walked away.",
      "Not understanding that co-sale rights meant a planned sale would be cut down pro rata across every investor.",
    ],
    negotiationPoints: [
      "Ask for permitted transfers for estate planning and a modest annual founder sale allowance.",
      "Ask for a notice and closing timetable short enough that a real buyer will wait it out.",
      "Ask for an explicit prohibited-transferee list naming competitors, distributors, co-packers and retailers.",
      "Ask that the restrictions terminate at a defined event rather than running indefinitely.",
      "Ask for symmetry where it is reasonable, so investor sales trigger something too.",
    ],
    redFlags: [
      "Restrictions binding founders only, with no permitted transfers of any kind.",
      "No prohibited-transferee list.",
      "A notice and closing process measured in months.",
      "Co-sale rights that can absorb an entire founder allocation.",
      "Restrictions with no termination event.",
    ],
    beforeYouSign: [
      "Every other transfer restriction already applying to your shares, read together with this one.",
      "The purchase agreement and any voting agreement, read alongside rather than separately.",
      "Your own honest view of when you might need liquidity and what this document would allow.",
    ],
    related: [
      "preferred-stock-purchase-agreement",
      "investor-rights-agreement",
      "right-of-first-refusal-agreement",
      "stock-transfer-restriction-agreement",
      "voting-agreement",
      "buy-sell-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "This permanently restricts what you can do with your own shares and interacts with every other transfer restriction you have signed, so it needs reading as a set rather than alone.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "advisory-equity-agreement",
    name: "Advisory Equity Agreement",
    category: "investors",
    subcategory: "Equity grants",
    aliases: ["Advisor Equity Grant", "Advisor Share Agreement", "Equity-for-Advice Agreement"],
    stages: ["pre-launch", "launch", "growth", "fundraising"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Company + Advisor",
    whenYouNeedIt:
      "When a category veteran, a former retail buyer or an ex-distributor executive agrees to help and wants equity instead of cash.",
    whatItDoes:
      "Grants a small stake in exchange for defined help over a defined period, with vesting so the equity tracks the work that actually happens.",
    whyItMatters:
      "CPG advisors are engaged for relationships — an introduction to a buyer, a co-packer with capacity, a distributor's regional manager. Those relationships either produce within two quarters or they do not, but the equity is permanent unless vesting says otherwise. Founders hand out advisor stakes early because they cost no cash, and the cumulative effect only becomes visible at the first priced round, when a meaningful block of the company belongs to people who made one phone call. Issuing equity to anyone is a regulated transaction, and where the advisor is helping with capital rather than customers, the questions get harder.",
    majorTerms: [
      "Size of the grant and its form",
      "Vesting schedule and cliff",
      "Defined deliverables or time commitment",
      "Term and how either side ends it",
      "Confidentiality",
      "Conflicts and other brands advised",
      "Assignment of anything created",
      "Whether the advisor may name you publicly and whether you may name them",
      "Expenses",
    ],
    clausesToWatch: [
      {
        clause: "Vesting and acceleration",
        why: "Without monthly vesting and a cliff, an advisor who stops responding after six weeks keeps the whole grant.",
      },
      {
        clause: "Deliverables",
        why: "'Strategic advice' is not a deliverable; naming the specific doors or the specific hours is the only way the grant means anything.",
      },
      {
        clause: "Conflicts",
        why: "An advisor working with a competing brand in the same category and the same retailer is a live conflict, and without a clause you have no way to raise it.",
      },
      {
        clause: "Use of the advisor's name",
        why: "Putting a well-known operator's name and former employer in an investor deck is a separate permission from the advisory relationship itself.",
      },
      {
        clause: "Introductions and additional payment",
        why: "If it is unclear whether an introduction triggers anything beyond the equity, you will be having that conversation after the introduction has already happened.",
      },
      {
        clause: "Termination and unvested equity",
        why: "Without a clean termination right, an advisory relationship that has stopped working continues to vest.",
      },
    ],
    cpgIssues: [
      "Advisors are usually engaged for one specific door — a buyer at a chain, a co-packer with capacity, a distributor's regional lead. Write the door into the agreement and vest against it.",
      "An advisor who also advises a competing brand selling into the same retailers is a real conflict, not a theoretical one, and the industry is small enough that it happens often.",
      "Using an advisor's name and their former employer in an investor deck, on your website or in a retailer presentation is a separate grant of rights you should have in writing.",
      "Anyone being compensated for introducing investors rather than customers raises a different and harder set of legal questions — take that to counsel before agreeing anything of that shape.",
      "Several small advisor grants made across one launch year add up to a meaningful block on the cap table before a single institutional dollar arrives.",
    ],
    founderMistakes: [
      "Granting equity for an introduction that never happened, with no vesting to claw anything back.",
      "Making four advisor grants in a single year and being surprised by the combined percentage at the priced round.",
      "Discovering an advisor was promoting a competing brand to the same buyer.",
      "Agreeing to pay an advisor in equity for bringing investors, without asking counsel whether that arrangement was permitted.",
      "Never recording the grant on the cap table or getting board approval for it.",
    ],
    negotiationPoints: [
      "Ask for monthly vesting with a cliff over a defined term, and no acceleration except on a sale.",
      "Write the deliverable down: the specific introductions, or the specific hours per month.",
      "Ask for a conflicts clause that names your category and your key accounts.",
      "Ask for a clean termination right with unvested equity returned automatically.",
      "Ask for confidentiality covering your cost sheet, your co-packer and your account performance.",
    ],
    redFlags: [
      "Equity granted for a single introduction, vesting immediately.",
      "No conflicts provision in a category where the advisor is already active.",
      "An advisor asking for a percentage of the capital they help you raise.",
      "A grant agreed verbally and never documented or approved.",
      "A stake large enough to matter with no time commitment attached to it.",
    ],
    beforeYouSign: [
      "Your current fully-diluted cap table including every prior advisor grant.",
      "A written list of what this advisor will actually do and by when.",
      "Board or member approval for the grant, obtained before it is promised.",
    ],
    related: [
      "advisor-equity-agreement",
      "advisory-board-agreement",
      "equity-incentive-plan",
      "stock-option-agreement",
      "cap-table-documentation",
      "board-observer-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Issuing equity to an advisor is a regulated issuance, and where the advisor is helping with capital rather than customers it raises further questions counsel must answer first.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "board-observer-agreement",
    name: "Board Observer Agreement",
    category: "investors",
    subcategory: "Investor rights & governance",
    aliases: ["Observer Rights Letter", "Board Observation Agreement", "Observer Side Letter"],
    stages: ["growth", "fundraising"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Company + Investor's Named Observer",
    whenYouNeedIt:
      "When an investor who is not getting a board seat asks to attend board meetings anyway — commonly in a side letter alongside a SAFE, or as part of a priced round package.",
    whatItDoes:
      "Lets a named individual attend board meetings and receive board materials without voting, and sets the limits around that: confidentiality, exclusion from sensitive sessions, and how the right ends.",
    whyItMatters:
      "An observer seat feels like a small concession and is one of the stickiest things you can grant. Everything a board discusses — co-packer pricing, a distributor you are about to terminate, an approach from an acquirer, a formulation problem — is heard by someone who is not a director of your company. In a category this small, where investors hold several brands and buyers move between retailers, that is a genuine commercial exposure rather than a governance nicety. The confidentiality, exclusion and termination terms are the only protection you have, and they have to be right at the start because the right usually outlasts the enthusiasm that granted it.",
    majorTerms: [
      "The observer named as an individual",
      "Which meetings they attend and which materials they receive",
      "Confidentiality obligations",
      "The company's right to exclude for conflicts or privileged sessions",
      "No voting and no veto",
      "Whether the right transfers if the investor sells",
      "Term and termination",
      "The maximum number of observers across all investors",
    ],
    clausesToWatch: [
      {
        clause: "Named individual versus the investor's designee",
        why: "A right held by a firm lets them send whoever they like, including someone who joins a competing brand's board next quarter.",
      },
      {
        clause: "Exclusion rights",
        why: "Without an express right to exclude, you cannot hold a conflicted discussion or take legal advice with the observer out of the room.",
      },
      {
        clause: "Confidentiality and internal sharing",
        why: "If the observer may share freely inside their own firm, your co-packer identity and landed cost travel to every brand that firm holds.",
      },
      {
        clause: "Transfer of the right",
        why: "An observer right that survives the investor selling their position leaves a stranger sitting in your board meetings.",
      },
      {
        clause: "Termination triggers",
        why: "Rights with no end point accumulate, and a board of five with three observers is a room where nobody says anything real.",
      },
    ],
    cpgIssues: [
      "Investors in food and beverage frequently hold several brands, so an observer hears your co-packer, your landed cost and your retailer terms while sitting on another brand's cap table.",
      "Decisions to terminate a distributor, move co-packers or change pack format happen at board level and are exactly the information a trade observer would find most valuable.",
      "An approach from an acquirer or a strategic partner is board business, and an observer hears it at the same moment you do.",
      "Observers who are operators tend to drift into instructing the team, which creates confusion about who the small brand's staff actually report to.",
      "Multiple observers turn a small brand's board meeting into a performance, and the real conversation moves to a phone call afterwards.",
    ],
    founderMistakes: [
      "Granting an observer seat on a small early cheque and never being able to remove it.",
      "Having no ability to exclude an observer whose firm backed a competing brand.",
      "Discovering the observer's firm was circulating the brand's numbers internally with no obligation not to.",
      "Ending up with three observers on a five-person board, so candid discussion moved outside the meeting entirely.",
    ],
    negotiationPoints: [
      "Ask that the observer is named as an individual, not as a firm designee.",
      "Ask for an explicit right to exclude for conflicts, competitive sensitivity and legal advice.",
      "Ask for written confidentiality binding the observer and anyone they report to.",
      "Ask that the right terminates on a defined event, including the investor falling below a stated holding.",
      "Ask for a cap on the total number of observers across all investors.",
    ],
    redFlags: [
      "A right granted to a firm that backs a competing brand, with no exclusion mechanism.",
      "No confidentiality obligation at all.",
      "A right that survives the investor selling their position.",
      "Observer rights attached to a very small cheque.",
      "Language permitting the observer to bring colleagues or guests.",
    ],
    beforeYouSign: [
      "A list of every other brand this investor is involved with.",
      "Your existing board composition and any observer rights already granted.",
      "A clear view of what your board actually discusses month to month.",
    ],
    related: [
      "investor-rights-agreement",
      "preferred-stock-purchase-agreement",
      "independent-director-agreement",
      "investor-nda",
      "advisory-board-agreement",
      "term-sheet",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "An observer hears confidential board discussion without being a director of your company, so the confidentiality, exclusion and termination terms are the only protection you will have.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "term-sheet",
    name: "Term Sheet",
    category: "investors",
    subcategory: "Process documents",
    aliases: ["Investment Term Sheet", "Heads of Terms", "Summary of Terms"],
    stages: ["growth", "fundraising"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Company + Lead Investor",
    whenYouNeedIt:
      "When an investor is ready to state their terms — the short document that arrives before the long-form paperwork and effectively decides the deal.",
    whatItDoes:
      "Sets out the economics and the control terms of a proposed investment on a few pages. Most of it is expressed as non-binding, but some provisions usually bind immediately, and everything else becomes the brief the definitive documents are drafted from.",
    whyItMatters:
      "Founders treat a term sheet as a formality because it says non-binding at the top. In practice it is the negotiation: once a valuation, a preference, a pool and a board structure are on paper, moving any of them is expensive and looks like bad faith. The parts that do bind are usually exclusivity and confidentiality, so signing takes you off the market while the investor completes diligence — and in CPG, a slow season or a lost authorisation during those weeks can change their mind while you have no alternatives left. Read the option pool and the preference before the headline number.",
    majorTerms: [
      "Valuation and amount raised",
      "The security being issued",
      "Liquidation preference",
      "Option pool size and placement",
      "Board composition",
      "Protective provisions",
      "Pro rata and information rights",
      "Conditions to closing",
      "Exclusivity period",
      "Confidentiality",
      "Expenses and reimbursement",
      "Expiry of the offer",
    ],
    clausesToWatch: [
      {
        clause: "Exclusivity period",
        why: "It is usually binding and it removes your alternatives for as long as it runs, which is the entire source of your negotiating position.",
      },
      {
        clause: "Expense reimbursement",
        why: "You may be agreeing to pay the investor's legal costs whether or not the deal closes, which for a brand funding a production run at the same time is real money.",
      },
      {
        clause: "Option pool line",
        why: "A single line specifying pool size and placement can move founder ownership more than a full turn on the valuation.",
      },
      {
        clause: "Conditions to closing",
        why: "Diligence conditions written broadly are effectively an option for the investor while you remain locked and off the market.",
      },
      {
        clause: "Liquidation preference line",
        why: "One sentence here decides who gets paid what at a modest exit, which is the most likely outcome for most brands.",
      },
      {
        clause: "'Definitive documents to include customary terms'",
        why: "Customary to whom is decided later by the party whose counsel is drafting, so anything you care about should be in the term sheet.",
      },
    ],
    cpgIssues: [
      "A no-shop signed just before your selling season takes you off the market during the months your numbers are at their best.",
      "Diligence in CPG surfaces missing paper — co-packer agreements, formula ownership, contractor IP assignments, quality records — so assume everything will be asked for and fix what you can before you sign.",
      "Expense reimbursement can be material for a brand that is simultaneously funding a production run, and it is often agreed without a cap.",
      "A strategic investor's term sheet sometimes bundles commercial terms — exclusivity, supply, a listing — into the summary, where they are hardest to see and easiest to concede.",
      "Revenue definitions matter: a valuation quoted against gross revenue, before deductions, spoils and trade spend, is describing a different business from the one that shows up in the accounts.",
    ],
    founderMistakes: [
      "Signing a long exclusivity and losing three months of fundraising momentum when the deal died.",
      "Not noticing an expense reimbursement obligation that applied whether or not the round closed.",
      "Agreeing pool size and placement at term sheet stage without modelling what it did to founder ownership.",
      "Letting the term sheet describe a valuation against a revenue number the company could not stand behind in diligence.",
    ],
    negotiationPoints: [
      "Ask for the shortest exclusivity that allows real diligence, with a hard end date and no automatic extension.",
      "Ask for a cap on expense reimbursement and that it applies only on closing.",
      "Model the pool and the preference before you sign, not after.",
      "Ask for the full diligence request list up front so you know what will be asked for.",
      "Ask that any commercial arrangement with the investor is documented separately.",
    ],
    redFlags: [
      "Exclusivity longer than the diligence genuinely requires.",
      "Uncapped expense reimbursement payable regardless of outcome.",
      "A term sheet that references commercial rights over your product.",
      "Conditions letting the investor walk for any reason while you stay locked.",
      "Pressure to sign the same day it arrives.",
    ],
    beforeYouSign: [
      "A fully-diluted model including the new pool and every convertible instrument.",
      "The investor's diligence request list.",
      "An honest inventory of your own paperwork gaps.",
      "Counsel's read of whichever sections are binding.",
    ],
    related: [
      "preferred-stock-purchase-agreement",
      "investor-letter-of-intent",
      "safe",
      "convertible-note",
      "investor-rights-agreement",
      "no-shop-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Parts of a term sheet bind you immediately and the rest sets terms that are very hard to move later, so counsel should read it before you sign rather than after.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "investor-letter-of-intent",
    name: "Letter of Intent (Investment)",
    category: "investors",
    subcategory: "Process documents",
    aliases: ["Investment LOI", "Letter of Intent to Invest", "Indication of Interest"],
    stages: ["growth", "fundraising"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Company + Prospective Strategic Investor",
    whenYouNeedIt:
      "When a strategic party — a distributor, a retailer's investment arm, a family office, an industry operator — wants to signal serious intent before diligence, often bundling an investment with a commercial relationship.",
    whatItDoes:
      "Records that both sides intend to do a deal and on roughly what terms, usually with a few binding provisions such as confidentiality and exclusivity, and everything else expressed as intent rather than commitment.",
    whyItMatters:
      "Strategic investment letters in CPG frequently carry commercial expectations alongside the money — distribution rights, a supply commitment, category exclusivity. Those rights are among the most valuable things you own and the easiest to concede inside a document nobody treats as real. Meanwhile the binding sections take you off the market and open your books, so a strategic party that later walks away leaves knowing your landed cost, your co-packer and your buyer relationships. Treat the commercial half as a separate negotiation from the first draft.",
    majorTerms: [
      "Proposed investment amount and structure",
      "Indicative valuation or cap",
      "Any commercial rights under discussion",
      "Diligence scope and timetable",
      "Exclusivity",
      "Confidentiality",
      "Which provisions are binding",
      "Expiry of the letter",
      "Costs",
      "Conditions to proceeding",
    ],
    clausesToWatch: [
      {
        clause: "Which paragraphs are binding",
        why: "A letter described as non-binding usually binds you on exclusivity, confidentiality and costs, and those are the provisions with real consequences.",
      },
      {
        clause: "Commercial rights described loosely",
        why: "A sentence about distribution or supply becomes the starting point for a negotiation you never intended to have, on terms you would not have offered on their own.",
      },
      {
        clause: "One-way confidentiality",
        why: "You are the party disclosing formulas, cost sheets and account data, so protection running only in the other direction protects nobody who needs it.",
      },
      {
        clause: "Diligence scope",
        why: "A strategic party asking for cost sheets and co-packer identity early is running a competitive intelligence exercise as much as a financial one.",
      },
      {
        clause: "Expiry",
        why: "Without one, a letter drifts for months while your other conversations go cold and your leverage quietly disappears.",
      },
    ],
    cpgIssues: [
      "A distributor's investment letter that also asks for territory exclusivity is two deals in one document, and only one of them is reversible.",
      "A co-packer investing while asking for production rights ties your manufacturing to your cap table, so a manufacturing dispute becomes a shareholder dispute.",
      "A retailer's investment arm gains visibility into your margins on the same products its buyers negotiate for.",
      "Diligence by a trade party covers your co-packer, your landed cost and your account-level velocity — the exact material a competitor would want.",
      "These letters tend to arrive when your velocity looks best and expire when it does not, which is worth remembering when you are deciding how long to be locked up.",
    ],
    founderMistakes: [
      "Signing a strategic letter containing an exclusivity promise, then being unable to sign a distributor in the same territory.",
      "Handing over full cost sheets under a confidentiality clause that ran only one way.",
      "Letting a letter run for months with no expiry while other conversations went cold.",
      "Treating 'non-binding' as meaning nothing in the document mattered.",
    ],
    negotiationPoints: [
      "Ask that every commercial right is moved out to a separate agreement with its own term and termination.",
      "Ask for mutual confidentiality and a defined, staged diligence list.",
      "Ask for a short exclusivity with a hard expiry and no automatic extension.",
      "Ask that the letter expires automatically if definitive documents are not agreed by a date.",
      "Ask for a return-or-destroy obligation covering everything you disclose.",
    ],
    redFlags: [
      "Commercial exclusivity written into an investment letter.",
      "Confidentiality running only in your direction.",
      "No expiry date anywhere in the document.",
      "Requests for formulas, cost sheets or co-packer identity before terms are agreed.",
      "A party who will not sign an NDA before diligence begins.",
    ],
    beforeYouSign: [
      "A signed NDA covering everything you will share.",
      "A written list of what you will and will not disclose at each stage.",
      "A clear internal position on which commercial rights are not for sale at any price.",
    ],
    related: [
      "term-sheet",
      "investor-nda",
      "safe",
      "preferred-stock-purchase-agreement",
      "no-shop-agreement",
      "letter-of-intent",
    ],
    attorneyReview: "yes",
    attorneyNote: null,
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "cap-table-documentation",
    name: "Cap Table Documentation",
    category: "investors",
    subcategory: "Equity records",
    aliases: ["Capitalization Table", "Cap Table", "Equity Ledger", "Stock Ledger"],
    stages: ["idea", "launch", "growth", "fundraising", "exit"],
    importance: "essential",
    entityTypes: ["any"],
    parties: "Company (maintained for founders, investors, counsel and buyers)",
    whenYouNeedIt:
      "From the first equity issued, and urgently before any raise, any option grant, or any conversation with a buyer.",
    whatItDoes:
      "Keeps an accurate, current record of who owns what: shares or units issued, options granted, every outstanding convertible instrument, and what each one becomes on conversion — backed by the executed documents and approvals behind each line.",
    whyItMatters:
      "The cap table is the first thing every investor and every buyer checks, and it is the document founders most often keep in a spreadsheet nobody has updated since the last handshake. Missing founder stock paperwork, undocumented friends-and-family cheques, promised-but-never-granted advisor equity and unmodelled convertible instruments all surface in diligence at the same time, and the cost is either a discount to the price or weeks of clean-up while a deal waits. Reconstructing an equity history years later, out of memory and email threads, is the most avoidable expensive exercise in early-stage CPG.",
    majorTerms: [
      "Issued shares or units by class and holder",
      "Option pool authorised, granted, exercised and remaining",
      "Every outstanding SAFE, note and warrant with its terms",
      "Fully-diluted totals",
      "Board or member approvals behind each issuance",
      "Vesting status and commencement dates by holder",
      "Transfer restrictions applying to each holding",
      "A conversion model for the next round",
    ],
    clausesToWatch: [
      {
        clause: "Authorisation behind each issuance",
        why: "Shares recorded on a spreadsheet with no matching approval are a diligence finding, and fixing them retroactively takes counsel and time you will not have.",
      },
      {
        clause: "Convertible instruments recorded only as footnotes",
        why: "A SAFE listed as a note at the bottom rather than modelled into the fully-diluted total is how founders end up surprised by their own dilution.",
      },
      {
        clause: "Promised but ungranted equity",
        why: "An unrecorded promise to a broker, an advisor or a distributor's principal reappears as a claim exactly when the company becomes valuable.",
      },
      {
        clause: "Vesting commencement dates",
        why: "If nobody can evidence when vesting started, nobody can say what is vested, and that question arrives during a sale.",
      },
      {
        clause: "Option pool arithmetic",
        why: "Granted, exercised, forfeited and remaining have to reconcile to the authorised pool, and when they do not, an option grant may not be what the holder thinks it is.",
      },
      {
        clause: "Side letters",
        why: "Rights promised outside the main documents do not appear on the table but do bind the company, and they surface at the worst time.",
      },
    ],
    cpgIssues: [
      "Friends-and-family money raised at farmers'-market stage — a cheque from a chef, a cousin, an early stockist — is the most common source of cap-table gaps in CPG, and it is almost never papered at the time.",
      "Equity promised to a broker, a distributor's principal or a co-packer contact in exchange for access is frequently agreed verbally and never granted, and it reappears years later as a claim.",
      "Brands that raise from their own customers accumulate many small holders, which makes later rounds slower, sale mechanics more expensive, and signature-gathering a genuine project.",
      "A trademark or a formula held in a founder's personal name is a cap-table-adjacent problem discovered in the same diligence review, and it is usually found first.",
      "Because CPG raises follow production seasons, instruments pile up between rounds and the fully-diluted number moves every time you take another cheque to fund a run.",
    ],
    founderMistakes: [
      "Keeping the cap table in a spreadsheet that omitted every outstanding convertible instrument.",
      "Promising equity in conversation and never granting it, then facing a claim once the brand became valuable.",
      "Granting options with no board approval behind them.",
      "Never recording vesting commencement dates, so nobody could say what was vested when a founder left.",
      "Discovering during diligence that founder shares had never actually been issued.",
    ],
    negotiationPoints: [
      "Ask counsel to reconcile the cap table against the corporate records before any raise, not during one.",
      "Ask that every promised grant is either documented properly or withdrawn in writing.",
      "Ask that every issuance has a matching approval on file before it goes on the table.",
      "Ask that the conversion model is refreshed before you accept another cheque.",
      "Ask that one named person owns the record and that there is only one version of it.",
    ],
    redFlags: [
      "A cap table with no convertible instruments modelled into the fully-diluted total.",
      "Equity promised but never documented or approved.",
      "Issuances with no matching board or member approval.",
      "Several versions of the table circulating with different totals.",
      "Vesting dates nobody can evidence with a signed document.",
    ],
    beforeYouSign: [
      "Every signed founder stock or membership document, matched against the table.",
      "Board or member approvals for every issuance and grant.",
      "The full set of executed SAFEs, notes, warrants and option agreements.",
      "A conversion model showing what happens at a realistic next-round price.",
    ],
    related: [
      "founder-stock-documents",
      "safe",
      "convertible-note",
      "preferred-stock-purchase-agreement",
      "equity-incentive-plan",
      "stock-option-agreement",
    ],
    attorneyReview: "yes",
    attorneyNote: null,
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "founder-stock-documents",
    name: "Founder Stock Documents",
    category: "investors",
    subcategory: "Equity records",
    aliases: ["Founder Shares Paperwork", "Founder Equity Documents", "Founder Stock Issuance"],
    stages: ["idea", "pre-launch", "fundraising"],
    importance: "essential",
    entityTypes: ["corporation"],
    parties: "Company + Founders",
    whenYouNeedIt:
      "At formation, as soon as the entity exists — and urgently if you are about to raise and cannot produce them.",
    whatItDoes:
      "The set of documents that actually puts founder equity in founders' hands: the board approval, the purchase or issuance paperwork, the consideration paid, the vesting terms, and the IP assignment that travels with the shares.",
    whyItMatters:
      "Founders routinely believe they own their shares because a spreadsheet says so. Ownership comes from executed documents, an approval behind them and consideration actually paid, and diligence checks all three. Missing founder stock paperwork is one of the most common findings in an early CPG raise, and correcting it late raises questions for the founders that only counsel and your accountant can answer. The vesting terms and the IP assignment that come with these documents are also what protect the company when a founder leaves in year one — which, in a business this hard, happens often.",
    majorTerms: [
      "Number of shares and class",
      "Price or contribution paid for them",
      "Board approval and the issuance record",
      "Vesting schedule, cliff and commencement date",
      "Company repurchase right on departure and how it is priced",
      "The IP assignment tied to the issuance",
      "Transfer restrictions",
      "Any time-sensitive filings counsel and your accountant identify",
    ],
    clausesToWatch: [
      {
        clause: "Vesting commencement date",
        why: "It decides how much a departing founder keeps, and a date that cannot be evidenced is a date a buyer will not accept.",
      },
      {
        clause: "Repurchase right and its price",
        why: "Without a defined repurchase mechanism and price, a founder who leaves in month five keeps a full stake and permanently dilutes everyone who stays.",
      },
      {
        clause: "The IP assignment attached to the issuance",
        why: "If the recipe, brand name and artwork developed before formation are not assigned by name, the company is selling a product it does not own.",
      },
      {
        clause: "Consideration actually paid",
        why: "Shares recorded as issued with nothing paid or contributed are a finding in every diligence review and are awkward to fix afterwards.",
      },
      {
        clause: "Acceleration on a sale",
        why: "Whether unvested founder equity accelerates at an exit changes what a buyer is buying and what the founders receive.",
      },
    ],
    cpgIssues: [
      "The pre-formation recipe, brand name, logo files and product photography have to come across with the stock issuance, or the company owns a business built on assets held personally.",
      "A trademark filed in a founder's personal name before the entity existed is found in the same review and takes real time to move.",
      "The founder who is the face of the brand — on pack, at demos, on social — creates a name and likeness question that belongs alongside the equity documents.",
      "Early CPG teams often add a 'co-founder' after the product already exists; without vesting on that grant, the addition is permanent from day one.",
      "The entity formed in a hurry to open a bank account for a co-packer deposit is frequently not the entity the shares were meant to sit in.",
    ],
    founderMistakes: [
      "Never signing anything and relying on a spreadsheet to prove ownership.",
      "Recording shares as issued with no consideration paid and no approval on file.",
      "Issuing founder equity with no vesting, so a departing founder kept everything.",
      "Leaving the recipe, the logo and the domain in a personal name after the entity existed.",
      "Missing a time-sensitive filing that counsel had flagged at the time of issuance.",
    ],
    negotiationPoints: [
      "Ask counsel to issue founder equity properly at formation, with approval and consideration recorded.",
      "Ask that vesting applies to every founder including you, with a cliff.",
      "Ask that every pre-formation asset is assigned by name on a schedule attached to the issuance.",
      "Ask about filing deadlines the moment shares are issued, not months later.",
      "Ask that the repurchase right and its price are written now, while everyone is still friendly.",
    ],
    redFlags: [
      "No executed founder documents of any kind.",
      "A vesting schedule backdated with no explanation or approval.",
      "Shares recorded as issued with nothing paid or contributed.",
      "An IP assignment that lists no actual assets.",
      "A founder who will not sign the assignment.",
    ],
    beforeYouSign: [
      "The entity's formation records and current charter documents.",
      "A written list of every pre-formation asset and whose name it currently sits in.",
      "Counsel's view on timing and on any deadlines that attach to the issuance.",
    ],
    related: [
      "founder-stock-purchase-agreement",
      "restricted-stock-purchase-agreement",
      "founder-vesting-agreement",
      "founder-ip-assignment",
      "cap-table-documentation",
      "piia",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Issuing founder equity is a regulated issuance with time-sensitive steps, and it is far more expensive to correct later than to do properly at formation.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
];

export default INVESTORS_DOCS;
